import Stack from "@mui/material/Stack";
import { ChangeScope } from "@repo/api";
import { BodyTextM, HeadingS, useTypedTranslation } from "@repo/ui";
import { type FC } from "react";
import { dateTimeFormat } from "../../functions/dates";
import { useChangeRequests } from "../../queryHooks";
import Download from "../Download";
import CollapsibleTable from "../Table/Collapsible";
import { TransactionSkeleton } from "../Transactions/SingleTransactionRow";

const ChangesTable: FC = () => {
    const { t } = useTypedTranslation();
    const titles = [
        t("profile.changes.productLine"),
        t("profile.changes.contractNumber"),
        t("profile.changes.changeType"),
    ];

    const result = useChangeRequests();

    if (result.isSuccess) {
        if (result.data.length === 0) {
            return <Stack sx={{ mt: 3 }}>{t("profile.changes.noChanges")}</Stack>;
        }

        const rows: {
            productType: string;
            productId: string;
            changeType: string;
            content: JSX.Element;
        }[] = result.data.map((change) => {
            const isClientChange = change.changeScope === ChangeScope.Client;

            return {
                productType: isClientChange
                    ? t("profile.changes.clientData")
                    : t(`profile.changes.productTypes.${change.productType}`),
                productId: isClientChange ? t("profile.changes.notApplicable") : change.productId,
                changeType: t(`profile.changes.changeTypes.${change.changeType}`),
                content: (
                    <>
                        <Stack>
                            <HeadingS mb={0} withoutScale>
                                {t("profile.changes.createdDate")}
                            </HeadingS>
                            <BodyTextM>{dateTimeFormat.format(new Date(change.createdDate))}</BodyTextM>
                        </Stack>
                        <Stack>
                            <HeadingS mb={0} withoutScale>
                                {t("profile.changes.resolvedDate")}
                            </HeadingS>
                            <BodyTextM>
                                {change.resolvedDate
                                    ? dateTimeFormat.format(new Date(change.resolvedDate))
                                    : t("profile.changes.notApplicable")}
                            </BodyTextM>
                        </Stack>
                        <Stack>
                            <HeadingS mb={0} withoutScale>
                                {t("profile.changes.state")}
                            </HeadingS>
                            <BodyTextM>{t(`profile.changes.statuses.${change.status}`)}</BodyTextM>
                        </Stack>
                        {change.additionalData && (
                            <>
                                {change.additionalData.zzjDocumentInfo && (
                                    <Stack>
                                        <HeadingS mb={2} withoutScale>
                                            {t("investmentStrategy.zzj")}
                                        </HeadingS>
                                        <Download
                                            key={change.additionalData.zzjDocumentInfo.downloadPath}
                                            {...change.additionalData.zzjDocumentInfo}
                                        />
                                    </Stack>
                                )}
                                <Stack>
                                    <HeadingS mb={2} withoutScale>
                                        {t("investmentStrategy.precontractualInfo")}
                                    </HeadingS>
                                    <Download
                                        key={change.additionalData.dpsPrecontractualInfo.downloadPath}
                                        isExternalUrl
                                        {...change.additionalData.dpsPrecontractualInfo}
                                    />
                                </Stack>
                            </>
                        )}
                    </>
                ),
            };
        });

        return <CollapsibleTable titles={titles} rows={rows} />;
    }

    return (
        <>
            {[...Array(5).keys()].map((_, index) => (
                <TransactionSkeleton key={index} />
            ))}
        </>
    );
};
export default ChangesTable;
